import type { StepConfig, FieldConfig, FormValues } from '../types';
import { evaluateConditions } from './conditions';

/**
 * Flattens nested step structure (handles children)
 */
export function flattenSteps(steps: StepConfig[]): StepConfig[] {
  const result: StepConfig[] = [];
  for (const step of steps) {
    result.push(step);
    if (step.children && step.children.length > 0) {
      result.push(...flattenSteps(step.children));
    }
  }
  return result;
}

/**
 * Gets visible steps based on showIf conditions
 */
export function getVisibleSteps(allSteps: StepConfig[], values: FormValues): StepConfig[] {
  return allSteps.filter((step) => evaluateConditions(step.showIf, values));
}

/**
 * Gets visible fields from a step based on field group and field-level visibility
 */
export function getVisibleFields(step: StepConfig, values: FormValues): FieldConfig[] {
  const visibleFields: FieldConfig[] = [];

  // Iterate through fieldGroups and get visible fields from each group
  if (step.fieldGroups) {
    for (const group of step.fieldGroups) {
      // Check if the group itself should be visible
      if (evaluateConditions(group.showIf, values)) {
        // Filter fields by their individual showIf conditions
        const visibleFieldsInGroup = group.fields.filter((field) =>
          evaluateConditions(field.showIf, values)
        );
        visibleFields.push(...visibleFieldsInGroup);
      }
    }
  }

  return visibleFields;
}

/**
 * Gets all fields from a step (regardless of visibility)
 */
export function getStepFields(step: StepConfig | undefined): FieldConfig[] {
  if (!step || !step.fieldGroups) return [];
  const fields: FieldConfig[] = [];
  step.fieldGroups.forEach((group) => {
    fields.push(...group.fields);
  });
  return fields;
}
